import * as React from "react";

import html2canvas from "html2canvas";
import jsPDF from "jspdf";

import { useTheme } from "@mui/material/styles";
import { Box, Typography, Grid } from "@mui/material";

import PaperFragment from "../../../../helper-components/misc/PaperFragment";
import { periodontalExportFileName } from "../../../../helper-functions/shared/exportFileName";

import PeriodontalTable from "./PeriodontalTable";
import PeriodontalStatistics from "./PeriodontalStatistics";
import ExaminationExportBar from "./ExaminationExportBar";

/**
 * ExaminationViewTable Component
 * Wrapper component that displays periodontal charts with export functionality
 *
 * @param {Object} props.teethData - The examination teeth data
 * @param {Object} props.patientInfo - Patient information
 * @param {boolean} props.sidebar.openAppDrawer - Whether the app drawer is open (affects export dimensions)
 */
export default function ExaminationViewTable({ teethData, summaryData = null, patientInfo = {}, openAppDrawer = false }) {
    const theme = useTheme();

    const [isExporting, setIsExporting] = React.useState(false);

    const exportFileName = (detail, extension) =>
        periodontalExportFileName(
            {
                title: "Periodontal Table",
                patient: patientInfo.patient,
                date: patientInfo.createdOn,
                detail
            },
            extension
        );

    // Get background color based on theme
    const getBgColor = () => (theme.palette.mode === "light" ? "#ffffff" : "#121212");

    // Fixed viewport settings for consistent exports across devices
    const EXPORT_CONFIG = {
        windowWidth: openAppDrawer ? 1280 : 1080,
        width: openAppDrawer ? 960 : 960,
        scale: 1.75,
        combinedScale: 1.75
    };

    /**
     * Capture element with fixed viewport settings
     */
    const captureElement = async (element, scale = EXPORT_CONFIG.scale) => {
        return await html2canvas(element, {
            scale,
            width: EXPORT_CONFIG.width,
            windowWidth: EXPORT_CONFIG.windowWidth,
            backgroundColor: getBgColor(),
            logging: false
        });
    };

    /**
     * Export a specific chart (upper or lower) as PNG
     */
    const exportToPNG = async (arch) => {
        setIsExporting(true);
        try {
            // Table IDs (4 tables in 2x2 grid)
            let tableIds = [];
            if (arch === "upper") tableIds = ["table-upper-right-buccal", "table-upper-right-palatal", "table-upper-left-buccal", "table-upper-left-palatal"];
            if (arch === "lower") tableIds = ["table-lower-right-buccal", "table-lower-right-lingual", "table-lower-left-buccal", "table-lower-left-lingual"];

            // Capture table canvases with fixed viewport
            const tableCanvases = [];
            for (const id of tableIds) {
                const element = document.getElementById(id);
                if (!element) {
                    console.error("Table element not found:", id);
                    return;
                }
                const canvas = await captureElement(element);
                tableCanvases.push(canvas);
            }

            // Capture statistics canvas
            const statsElement = document.getElementById("chart-statistics");
            const statsCanvas = statsElement ? await captureElement(statsElement) : null;

            // Layout: 2x2 grid for tables, then statistics centered below
            const gap = 10;
            const cols = 2;
            const maxTableWidth = Math.max(...tableCanvases.map((c) => c.width));
            const gridWidth = maxTableWidth * cols + gap;

            // Calculate table grid height (2 rows)
            const row1Height = Math.max(tableCanvases[0]?.height || 0, tableCanvases[1]?.height || 0);
            const row2Height = Math.max(tableCanvases[2]?.height || 0, tableCanvases[3]?.height || 0);
            const tableGridHeight = row1Height + row2Height + gap;

            // Total dimensions
            const totalWidth = Math.max(gridWidth, statsCanvas?.width || 0);
            const totalHeight = tableGridHeight + gap + (statsCanvas?.height || 0);

            const combinedCanvas = document.createElement("canvas");
            combinedCanvas.width = totalWidth;
            combinedCanvas.height = totalHeight;
            const ctx = combinedCanvas.getContext("2d");

            // Fill background
            ctx.fillStyle = getBgColor();
            ctx.fillRect(0, 0, combinedCanvas.width, combinedCanvas.height);

            // Draw tables in 2x2 grid (centered if narrower than stats)
            const gridOffsetX = (totalWidth - gridWidth) / 2;

            // Row 1: tables 0 and 1
            if (tableCanvases[0]) ctx.drawImage(tableCanvases[0], gridOffsetX, 0);
            if (tableCanvases[1]) ctx.drawImage(tableCanvases[1], gridOffsetX + maxTableWidth + gap, 0);

            // Row 2: tables 2 and 3
            if (tableCanvases[2]) ctx.drawImage(tableCanvases[2], gridOffsetX, row1Height + gap);
            if (tableCanvases[3]) ctx.drawImage(tableCanvases[3], gridOffsetX + maxTableWidth + gap, row1Height + gap);

            // Draw statistics centered at bottom
            if (statsCanvas) {
                const statsX = (totalWidth - statsCanvas.width) / 2;
                ctx.drawImage(statsCanvas, statsX, tableGridHeight + gap);
            }

            // Create download link
            const link = document.createElement("a");
            link.download = exportFileName(`${arch === "upper" ? "Upper" : "Lower"} PNG`, "png");
            link.href = combinedCanvas.toDataURL("image/png");
            link.click();
        } catch (error) {
            console.error("Error exporting to PNG:", error);
        } finally {
            setIsExporting(false);
        }
    };

    /**
     * Export a specific chart (upper or lower) as PDF
     */
    const exportToPDF = async (arch) => {
        setIsExporting(true);
        try {
            // Table IDs (4 tables in 2x2 grid)
            let tableIds = [];
            if (arch === "upper") tableIds = ["table-upper-right-buccal", "table-upper-right-palatal", "table-upper-left-buccal", "table-upper-left-palatal"];
            if (arch === "lower") tableIds = ["table-lower-right-buccal", "table-lower-right-lingual", "table-lower-left-buccal", "table-lower-left-lingual"];

            // Capture table canvases with fixed viewport
            const tableCanvases = [];
            for (const id of tableIds) {
                const element = document.getElementById(id);
                if (!element) {
                    console.error("Table element not found:", id);
                    return;
                }
                const canvas = await captureElement(element);
                tableCanvases.push(canvas);
            }

            // Capture statistics canvas
            const statsElement = document.getElementById("chart-statistics");
            const statsCanvas = statsElement ? await captureElement(statsElement) : null;

            // Layout: 2x2 grid for tables, then statistics centered below
            const gap = 10;
            const cols = 2;
            const maxTableWidth = Math.max(...tableCanvases.map((c) => c.width));
            const gridWidth = maxTableWidth * cols + gap;

            // Calculate table grid height (2 rows)
            const row1Height = Math.max(tableCanvases[0]?.height || 0, tableCanvases[1]?.height || 0);
            const row2Height = Math.max(tableCanvases[2]?.height || 0, tableCanvases[3]?.height || 0);
            const tableGridHeight = row1Height + row2Height + gap;

            // Total dimensions
            const totalWidth = Math.max(gridWidth, statsCanvas?.width || 0);
            const totalHeight = tableGridHeight + gap + (statsCanvas?.height || 0);

            const combinedCanvas = document.createElement("canvas");
            combinedCanvas.width = totalWidth;
            combinedCanvas.height = totalHeight;
            const ctx = combinedCanvas.getContext("2d");

            // Fill background
            ctx.fillStyle = getBgColor();
            ctx.fillRect(0, 0, combinedCanvas.width, combinedCanvas.height);

            // Draw tables in 2x2 grid (centered if narrower than stats)
            const gridOffsetX = (totalWidth - gridWidth) / 2;

            // Row 1: tables 0 and 1
            if (tableCanvases[0]) ctx.drawImage(tableCanvases[0], gridOffsetX, 0);
            if (tableCanvases[1]) ctx.drawImage(tableCanvases[1], gridOffsetX + maxTableWidth + gap, 0);

            // Row 2: tables 2 and 3
            if (tableCanvases[2]) ctx.drawImage(tableCanvases[2], gridOffsetX, row1Height + gap);
            if (tableCanvases[3]) ctx.drawImage(tableCanvases[3], gridOffsetX + maxTableWidth + gap, row1Height + gap);

            // Draw statistics centered at bottom
            if (statsCanvas) {
                const statsX = (totalWidth - statsCanvas.width) / 2;
                ctx.drawImage(statsCanvas, statsX, tableGridHeight + gap);
            }

            // Create PDF - landscape A4, maximize width usage
            const pdf = new jsPDF({
                orientation: "landscape",
                unit: "mm",
                format: "a4",
                compress: true
            });

            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const margin = 3;
            const availableWidth = pageWidth - margin * 2;
            const availableHeight = pageHeight - margin * 2;

            // Scale to use full available width
            let imgWidth = availableWidth;
            let imgHeight = (combinedCanvas.height * availableWidth) / combinedCanvas.width;

            // Only scale down if height exceeds page
            if (imgHeight > availableHeight) {
                imgHeight = availableHeight;
                imgWidth = (combinedCanvas.width * availableHeight) / combinedCanvas.height;
            }

            // Center vertically only (use full width)
            const xPos = (pageWidth - imgWidth) / 2;
            const yPos = (pageHeight - imgHeight) / 2;
            pdf.addImage(combinedCanvas.toDataURL("image/png"), "PNG", xPos, yPos, imgWidth, imgHeight);
            pdf.save(exportFileName(`${arch === "upper" ? "Upper" : "Lower"} PDF`, "pdf"));
        } catch (error) {
            console.error("Error exporting to PDF:", error);
        } finally {
            setIsExporting(false);
        }
    };

    /**
     * Export both upper and lower tables as a combined PDF (all on one portrait A4 page)
     */
    const exportCombinedPDF = async () => {
        setIsExporting(true);
        try {
            const upperIds = ["table-upper-right-buccal", "table-upper-right-palatal", "table-upper-left-buccal", "table-upper-left-palatal"];
            const lowerIds = ["table-lower-right-buccal", "table-lower-right-lingual", "table-lower-left-buccal", "table-lower-left-lingual"];

            // Capture all tables with fixed viewport
            const allCanvases = [];
            for (const id of [...upperIds, ...lowerIds]) {
                const element = document.getElementById(id);
                if (!element) {
                    console.error("Table element not found:", id);
                    return;
                }
                const canvas = await captureElement(element, EXPORT_CONFIG.combinedScale);
                allCanvases.push(canvas);
            }

            // Capture statistics
            const statsElement = document.getElementById("chart-statistics");
            const statsCanvas = statsElement ? await captureElement(statsElement, EXPORT_CONFIG.combinedScale) : null;

            // Layout: 2x2 grid for upper, 2x2 grid for lower, then statistics centered
            const gap = 5;
            const cols = 2;

            // Upper arch (first 4 canvases)
            const upperCanvases = allCanvases.slice(0, 4);
            const upperMaxWidth = Math.max(...upperCanvases.map((c) => c.width));
            const upperRow1Height = Math.max(upperCanvases[0]?.height || 0, upperCanvases[1]?.height || 0);
            const upperRow2Height = Math.max(upperCanvases[2]?.height || 0, upperCanvases[3]?.height || 0);
            const upperGridHeight = upperRow1Height + upperRow2Height + gap;

            // Lower arch (last 4 canvases)
            const lowerCanvases = allCanvases.slice(4, 8);
            const lowerMaxWidth = Math.max(...lowerCanvases.map((c) => c.width));
            const lowerRow1Height = Math.max(lowerCanvases[0]?.height || 0, lowerCanvases[1]?.height || 0);
            const lowerRow2Height = Math.max(lowerCanvases[2]?.height || 0, lowerCanvases[3]?.height || 0);
            const lowerGridHeight = lowerRow1Height + lowerRow2Height + gap;

            // Total dimensions
            const maxTableWidth = Math.max(upperMaxWidth, lowerMaxWidth);
            const gridWidth = maxTableWidth * cols + gap;
            const totalWidth = Math.max(gridWidth, statsCanvas?.width || 0);
            const totalHeight = upperGridHeight + gap + lowerGridHeight + gap + (statsCanvas?.height || 0);

            const combinedCanvas = document.createElement("canvas");
            combinedCanvas.width = totalWidth;
            combinedCanvas.height = totalHeight;
            const ctx = combinedCanvas.getContext("2d");

            ctx.fillStyle = getBgColor();
            ctx.fillRect(0, 0, combinedCanvas.width, combinedCanvas.height);

            const gridOffsetX = (totalWidth - gridWidth) / 2;

            // Draw upper arch tables (2x2)
            let currentY = 0;
            if (upperCanvases[0]) ctx.drawImage(upperCanvases[0], gridOffsetX, currentY);
            if (upperCanvases[1]) ctx.drawImage(upperCanvases[1], gridOffsetX + maxTableWidth + gap, currentY);
            currentY += upperRow1Height + gap;
            if (upperCanvases[2]) ctx.drawImage(upperCanvases[2], gridOffsetX, currentY);
            if (upperCanvases[3]) ctx.drawImage(upperCanvases[3], gridOffsetX + maxTableWidth + gap, currentY);
            currentY += upperRow2Height + gap;

            // Draw lower arch tables (2x2)
            if (lowerCanvases[0]) ctx.drawImage(lowerCanvases[0], gridOffsetX, currentY);
            if (lowerCanvases[1]) ctx.drawImage(lowerCanvases[1], gridOffsetX + maxTableWidth + gap, currentY);
            currentY += lowerRow1Height + gap;
            if (lowerCanvases[2]) ctx.drawImage(lowerCanvases[2], gridOffsetX, currentY);
            if (lowerCanvases[3]) ctx.drawImage(lowerCanvases[3], gridOffsetX + maxTableWidth + gap, currentY);
            currentY += lowerRow2Height + gap;

            // Draw statistics centered at bottom
            if (statsCanvas) {
                const statsX = (totalWidth - statsCanvas.width) / 2;
                ctx.drawImage(statsCanvas, statsX, currentY);
            }

            // Create PDF - portrait A4, fit all on one page
            const pdf = new jsPDF({
                orientation: "portrait",
                unit: "mm",
                format: "a4",
                compress: true
            });

            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const margin = 3;
            const availableWidth = pageWidth - margin * 2;
            const availableHeight = pageHeight - margin * 2;

            let imgWidth = availableWidth;
            let imgHeight = (combinedCanvas.height * availableWidth) / combinedCanvas.width;

            // Scale down if height exceeds page
            if (imgHeight > availableHeight) {
                imgHeight = availableHeight;
                imgWidth = (combinedCanvas.width * availableHeight) / combinedCanvas.height;
            }

            // Center on page
            const xPos = (pageWidth - imgWidth) / 2;
            const yPos = (pageHeight - imgHeight) / 2;
            pdf.addImage(combinedCanvas.toDataURL("image/png"), "PNG", xPos, yPos, imgWidth, imgHeight);

            pdf.save(exportFileName(null, "pdf"));
        } catch (error) {
            console.error("Error exporting combined PDF:", error);
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <>
            {/* Export controls */}
            <PaperFragment
                p={2}
                center
                title={"Export Options"}
                value={
                    <Box sx={{ mb: 1, display: "flex", flexDirection: "column", gap: 2, alignItems: "center" }}>
                        <ExaminationExportBar
                            label="EXPORT TABLE PDF"
                            isExporting={isExporting}
                            onExportPdf={exportCombinedPDF}
                            onUpperPng={() => exportToPNG("upper")}
                            onUpperPdf={() => exportToPDF("upper")}
                            onLowerPng={() => exportToPNG("lower")}
                            onLowerPdf={() => exportToPDF("lower")}
                        />

                        {isExporting && (
                            <Typography variant="body2" color="text.secondary">
                                Exporting... Please wait.
                            </Typography>
                        )}
                    </Box>
                }
            />

            {/* Upper arch tables */}
            <Grid item xs={12} xl={6}>
                <PaperFragment p={0.5} value={<PeriodontalTable state={teethData} setState={() => {}} variant="upper-right-buccal" readOnly />} />
            </Grid>
            <Grid item xs={12} xl={6}>
                <PaperFragment p={0.5} value={<PeriodontalTable state={teethData} setState={() => {}} variant="upper-right-palatal" readOnly />} />
            </Grid>
            <Grid item xs={12} xl={6}>
                <PaperFragment p={0.5} value={<PeriodontalTable state={teethData} setState={() => {}} variant="upper-left-buccal" readOnly />} />
            </Grid>
            <Grid item xs={12} xl={6}>
                <PaperFragment p={0.5} value={<PeriodontalTable state={teethData} setState={() => {}} variant="upper-left-palatal" readOnly />} />
            </Grid>

            {/* Lower arch tables */}
            <Grid item xs={12} xl={6}>
                <PaperFragment p={0.5} value={<PeriodontalTable state={teethData} setState={() => {}} variant="lower-right-buccal" readOnly />} />
            </Grid>
            <Grid item xs={12} xl={6}>
                <PaperFragment p={0.5} value={<PeriodontalTable state={teethData} setState={() => {}} variant="lower-right-lingual" readOnly />} />
            </Grid>
            <Grid item xs={12} xl={6}>
                <PaperFragment p={0.5} value={<PeriodontalTable state={teethData} setState={() => {}} variant="lower-left-buccal" readOnly />} />
            </Grid>
            <Grid item xs={12} xl={6}>
                <PaperFragment p={0.5} value={<PeriodontalTable state={teethData} setState={() => {}} variant="lower-left-lingual" readOnly />} />
            </Grid>

            {/* Statistics (combined for both arches) */}
            <Grid item xs={12}>
                <PeriodontalStatistics teethData={teethData} summaryData={summaryData} patientInfo={patientInfo} />
            </Grid>
        </>
    );
}
